import Heading from "../../../components/Heading";
import React, {useCallback, useMemo} from "react";
import {CountData, StoredTemplate} from "../../../types/Individualization";
import {Article} from "../../../types/Article";
import {useTranslation} from "react-i18next";

type PriceOverviewProps = {
    individualization_id: number;
    article: Article;
    priceArray: CountData[];
    priceList: {count: number, price: number}[];
    templates: StoredTemplate[];
    price: [string, string]
    quantity: number
}

const PriceOverview: React.FC<PriceOverviewProps> = (
    {
        individualization_id,
        article,
        priceArray,
        priceList,
        templates,
        price,
        quantity
    }
) => {
    const { t } = useTranslation(['customizer'])
    const articlePrice = useMemo(() => {
        return priceArray.find((ele) => (ele.id === article.kArtikel && ele.type === 'article'))
    }, [article, priceArray])

    const individualizationPrice = useMemo(() => {
        return priceArray.find((ele) => (ele.id === individualization_id && ele.type === 'individualization'))
    }, [individualization_id, priceArray])

    const getPriceForElement = useCallback((id: number, type: string) => {
        return priceArray.find((e) => (e.id === id && e.type === type))
    }, [priceArray])

    const getGraduatedPrices = useMemo(() => {
        return priceList.reverse().map((graduatedPrice) => {
            return (
                <tr key={"price-" +graduatedPrice.count}>
                    <td>{graduatedPrice.count}</td>
                    <td>
                        <span className="bulk-price">{((graduatedPrice.price/100).toFixed(2) + ' €').replace('.', ',')}</span>
                        <span className="footnote-reference">*</span>
                    </td>
                </tr>
            )
        })
    }, [priceList])

    return (
        <div style={{display: 'flex', flexDirection: 'column', marginTop: '3rem'}}>
            {priceList.length > 1 &&
                <div className={'mb-3'}>
                    <Heading level={3}>{t("graduatedPrices")}</Heading>
                    <table className="table table-sm">
                        <thead>
                            <tr>
                                <th>{t("minimumAmount")}</th>
                                <th>{t("piecePrice")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {getGraduatedPrices}
                        </tbody>
                    </table>
                </div>
            }
            <Heading level={3} classes={'mb-3'}>{t("summary")}</Heading>
            {articlePrice &&
                <div style={{display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'baseline'}}>
                    <Heading level={4}>{t("itemPrice")}</Heading>
                    <span dangerouslySetInnerHTML={{__html: articlePrice.price}}/>
                </div>
            }
            {individualizationPrice &&
                <div style={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'space-between',
                    alignItems: 'baseline'
                }}>
                    <Heading level={4}>{t("globalSurcharge")}</Heading>
                    <span dangerouslySetInnerHTML={{__html: individualizationPrice.price}}/>
                </div>
            }
            {templates.map((template) => {
                return (
                    <div key={template.id} style={{width: '100%', display: 'flex', flexDirection: 'column'}}>
                        {(getPriceForElement(template.id, 'template') || template.spots.filter((s) => getPriceForElement(s.id, 'spot') !== undefined).length > 0) &&
                            <div key={'template_' + template.id} style={{display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'baseline'}}>
                                <Heading level={4}>{template.name}</Heading>
                                <span dangerouslySetInnerHTML={{__html: getPriceForElement(template.id, 'template')?.price ?? 'inklusive'}}/>
                            </div>
                        }
                        {template.spots.map((spot) => {
                            return getPriceForElement(spot.id, 'spot')  ? (
                                <div key={'spot_'+ spot.id} style={{display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'baseline'}}>
                                    <p>{spot.name}</p>
                                    <span dangerouslySetInnerHTML={{__html: getPriceForElement(spot.id, 'spot')?.price ?? '-'}}/>
                                </div>
                            ) : null
                        })}
                    </div>
                )
            })}
            <div style={{display: 'flex', flexDirection: 'column', paddingTop: '1rem', borderTop: '1px solid black', width: '100%',}}>
                <div style={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'space-between',
                    alignItems: 'baseline'
                }}>
                    <span>{t("AmountXPerPiece")}</span>
                    <div>
                        <span dangerouslySetInnerHTML={{__html: quantity + ' x ' + price[0]}}/>
                        <span className="footnote-reference">*</span>
                    </div>
                </div>
                <div style={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'space-between',
                    alignItems: 'baseline'
                }}>
                    <Heading level={4}>{t("totalAmount")}</Heading>
                    <div>
                        <span dangerouslySetInnerHTML={{__html: price[1]}}/>
                        <span className="footnote-reference">*</span>
                    </div>
                </div>
            </div>
            <span className="small">{t("allPricesIncludesShippingCosts")}</span>
        </div>
    )
}

export default PriceOverview
